import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { AdminApiService } from 'projects/admin/src/api-service/api.service';

@Component({
  selector: 'app-game-type-retention',
  templateUrl: './game-type-retention.component.html',
  styleUrls: ['./game-type-retention.component.scss'],
})
export class GameTypeRetentionComponent implements OnInit, OnChanges {

  @Input() gameTypeId: number | undefined;
  @Input() start: moment.Moment;
  @Input() end: moment.Moment;
  @Input() filterByCompetition: boolean;
  @Input() affiliateId: number;

  selectedGameTypeId: number;
  filteredByCompetition: boolean;

  blob: any = {};
  games: any[];
  rounds: any[];
  gamePlayers: any = {};
  colourDegrees: number[];

  isSequential: boolean = true;

  showSequentialDescription: boolean = false;
  showRelativeToFirstDescription: boolean = false;

  buildingMatrix: boolean = true;

  hasBuiltMatrix: boolean = false;
  hasChanged: boolean = false;

  refreshData: boolean = false;

  constructor(private apiService: AdminApiService, private spinner: NgxSpinnerService) { }

  ngOnInit(): void { }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.gameTypeId || changes.filterByCompetition) {
      this.refreshData = true;
    }

    this.hasChanged = true;
  }

  generateTable = (): void => {

    if (this.gameTypeId === undefined) return;

    this.selectedGameTypeId = this.gameTypeId;
    this.filteredByCompetition = this.filterByCompetition;

    this.hasChanged = false;

    this.colourDegrees = [];

    this.buildingMatrix = true;
    
    this.blob = {
      'All': {}
    };

    if (this.refreshData) {

      if (this.filterByCompetition) {

        this.getSeriesGames().then(() => {
          this.buildRetentionMatrix();
        })

      } else {

        this.getGames().then(() => {

          this.filterRoundsByDate();
          this.buildRetentionMatrix();
        })
      }
    } else {

      this.filterRoundsByDate();
      this.buildRetentionMatrix();
    }

    this.refreshData = false;
  }

  getSeriesGames = (): Promise<void> => {

    return new Promise((resolve, reject) => {

      this.spinner.show();

      Object.keys(this.blob).forEach(k => {
        this.blob[k] = {
          gameIds: [],
          gameIdToRoundName: {}
        };
      })

      if (!this.blob.hasOwnProperty('All')) {
        this.blob['All'] = {
          gameIds: [],
          gameIdToRoundName: {}
        }
      }

      this.apiService.getRootSeriesWithChildrenGames(this.affiliateId).subscribe((res: any) => {

        res.data?.forEach(competition => {

          competition.children?.forEach(round => {

            round.series?.games?.forEach((g, idx) => {

              if ((this.gameTypeId === 0 || this.gameTypeId === g.gameTypeId) && ['REPORTED', 'ARCHIVED'].includes(g.state)) {

                this.blob['All'].gameIds.push(g.id)
                this.blob['All'][g.id] = {
                  name: g.name
                }
                this.blob['All'].gameIdToRoundName[g.id] = round.series?.name

                if (!this.blob.hasOwnProperty(competition.name)) {
                  this.blob[competition.name] = {
                    gameIds: [],
                    gameIdToRoundName: {}
                  };
                }

                this.blob[competition.name].gameIds.push(g.id)
                this.blob[competition.name][g.id] = {
                  name: g.name
                }
                this.blob[competition.name].gameIdToRoundName[g.id] = round.series?.name
              }
            })
          })
        })

        Object.keys(this.blob).forEach(k => {
          this.blob[k].gameIds.sort((a, b) => a - b);
        })

        let colourDegrees = 0;
        const colourDiv = 360 / this.blob['All'].gameIds.length;

        const gamePlayerPromises: any[] = [];

        this.blob['All'].gameIds.forEach(id => {

          colourDegrees += colourDiv;

          this.colourDegrees.push(colourDegrees);

            gamePlayerPromises.push(this.getGamePlayers(id));
        });

        Promise.all(gamePlayerPromises).then(() => {
          this.spinner.hide();
          resolve();
        });

      }, err => {
        this.spinner.hide();
        resolve();
      })
    })
  }

  getGames = (): Promise<void> => {

    return new Promise((resolve, reject) => {

      this.spinner.show();

      this.blob['All'] = {
        gameIds: []
      }

      let f = this.apiService.getReportedAndArchivedGames();
      if (this.gameTypeId > 0) {
        f = this.apiService.getGamesByGameTypeId(this.gameTypeId);
      }

      f.subscribe((res: any) => {

        this.games = res.data?.games;
        this.rounds = res.data?.rounds;
        this.gamePlayers = {};

        this.blob['All'].gameIds = this.games?.map(g => g.id);

        const gamePlayerPromises: any[] = [];

        this.blob['All'].gameIds.forEach(id => {

          const g = this.games.find(g => g.id === id);
          if (g) {
            gamePlayerPromises.push(this.getGamePlayers(id));
          }
        });

        Promise.all(gamePlayerPromises).then(() => {
          this.spinner.hide();
          resolve();
        });

      }, err => {
        this.spinner.hide();
        resolve();
      })
    })
  }

  filterRoundsByDate = (): void => {

    if (!this.games) return;
    if (!this.rounds) return;

    this.spinner.show();

    const gameIds: number[] = []
    this.rounds.forEach(r => {

      const openTime = r.actions?.find(a => a.name === 'MOVE_READY_TO_OPEN')?.scheduled;
      if (openTime && moment(openTime).isAfter(this.start) && moment(openTime).isBefore(this.end)) {
        gameIds.push(r.gameId);
      }
    });

    this.blob['All'].gameIds = gameIds.sort((a, b) => a - b);

    let colourDegrees = 0;
    const colourDiv = 360 / gameIds.length;

    gameIds.forEach(id => {

      colourDegrees += colourDiv;

      this.colourDegrees.push(colourDegrees);

      const g = this.games.find(g => g.id === id);
      if (g) {
        this.blob['All'][id] = {
          name: g.name
        }
      }
    });

    this.spinner.hide();
  }

  getGamePlayers = (gameId: number): Promise<void> => {

    return new Promise((resolve, reject) => {
      this.apiService.getGamePlayers(gameId).subscribe((res: any) => {
        this.gamePlayers[gameId] = { players: {} }
        res.data.forEach(p => {
          this.gamePlayers[gameId].players[p.playerId] = true;
        })
        resolve()
      }, err => {
        resolve();
      })
    })
  }

  buildRetentionMatrix = (): void => {

    Object.keys(this.blob).forEach(k => {

      const gameIds = this.blob[k].gameIds;

      gameIds.forEach((gameId, idx) => {

        this.blob[k][gameId].retainedPlayers = [Object.keys(this.gamePlayers[gameId].players).length];

        let players = this.gamePlayers[gameId].players;

        while (idx > 0) {
          const previousGamePlayers = this.gamePlayers[gameIds[idx - 1]].players;

          let retainedPlayers = {};
          Object.keys(players).forEach(p => {
            if (previousGamePlayers[p]) {
              retainedPlayers[p] = true;
            }
          })

          this.blob[k][this.blob[k].gameIds[idx - 1]].retainedPlayers.push(Object.keys(retainedPlayers).length);

          if (this.isSequential) {
            players = retainedPlayers;
          }

          idx--;
        }
      });
    })

    this.buildingMatrix = false;
    this.hasBuiltMatrix = true;
  }

  isFinalColumn = (index, retainedPlayers): boolean => {

    return index === Object.keys(retainedPlayers).length - 1;
  }

  isFinalBlob = (index): boolean => {

    return index === Object.keys(this.blob).length - 1;
  }

  getPercentageRaw = (retainedPlayers, originalPlayers): number => {

    return (100 * retainedPlayers / originalPlayers);
  }

  getPercentage = (retainedPlayers, originalPlayers): string => {

    return this.getPercentageRaw(retainedPlayers, originalPlayers).toFixed(0);
  }

  getAveragePercentage = (key, idx, idx2): string => {

    if (idx < 2) return '';

    const total = idx - 1;

    let percRaw = 0

    while (idx > 1) {

      const percentageRaw = this.getPercentageRaw(this.blob[key][this.blob[key].gameIds[idx - 2]].retainedPlayers[idx2 + 1], this.blob[key][this.blob[key].gameIds[idx - 2]].retainedPlayers[0]);
      percRaw += percentageRaw;
      idx--;
    }

    return (percRaw / total).toFixed()
  }

  getDifferenceInRetainedPlayers = (key, idx, idx2): number => {

    return this.blob[key][this.blob[key].gameIds[idx]].retainedPlayers[idx2] - this.blob[key][this.blob[key].gameIds[idx - 1]].retainedPlayers[idx2];
  }

  getDifferenceInRetainedPlayersFormatted = (key, idx, idx2): string => {

    const diff = this.getDifferenceInRetainedPlayers(key, idx, idx2)
    if (diff < 0) {
      return diff.toString()
    }

    return `+${diff}`
  }

  isButtonDisabled = (): boolean => {

    return (this.gameTypeId === undefined || !this.start || !this.end) || (this.hasBuiltMatrix && !this.hasChanged);
  }

  getNumberOfGamesInRound = (key, gameId): number => {

    const roundName = this.blob[key].gameIdToRoundName[gameId];

    let count = 0;

    Object.values(this.blob[key].gameIdToRoundName).forEach(x => {
      if (x === roundName) {
        count += 1
      }
    });

    return count;
  }

}
